// React
import { useEffect } from "react"

// React Query
import { useMutation, useQueryClient } from "@/lib/query"

// GraphQL
import { queryFetcher } from "@/api/graphql/codegen.fetcher"

// Context
import { useHiddenGetParamsContext } from "@/context/useHiddenGetParams"

/**
 * TEMPORARY: hand-written stand-in for the generated
 * `useScaStatusRefreshMutation`.
 *
 * Codegen introspects the live schema, so the hook cannot be generated until
 * `sca_status_refresh` is deployed. Once it is, run `pnpm codegen:graphql` and
 * replace everything down to `useScaStatusRefresh` with an import of the
 * generated hook; `src/context/scaStatusRefresh.graphql` already holds the
 * document codegen will pick up.
 */
const SCA_STATUS_REFRESH_DOCUMENT = `
    mutation ScaStatusRefresh {
  sca_status_refresh {
    refreshed
    mangopay_sca {
      sca_enrolled
      recipient_status
    }
  }
}
    `

type ScaStatusRefreshMutation = {
	sca_status_refresh?: {
		refreshed?: boolean | null
	} | null
}

const useScaStatusRefresh = () =>
	useMutation<ScaStatusRefreshMutation, unknown, undefined>({
		mutationKey: ["ScaStatusRefresh"],
		mutationFn: () =>
			queryFetcher<ScaStatusRefreshMutation, undefined>(
				SCA_STATUS_REFRESH_DOCUMENT,
			)(),
		// One bounded retry: the mutation talks to Mangopay, so a single
		// transient failure should not leave the user staring at a stale status.
		retry: 1,
	})

// `controlStatus` is captured once on return and then kept in context for the
// rest of the session. A per-mount ref would reset every time this component
// unmounts/remounts (e.g. the auth status briefly flips to AUTHENTICATING and
// back), re-firing the Mangopay-touching mutation. A module-level flag makes the
// reconcile genuinely one-shot per session, surviving remounts.
let hasReconciledScaThisSession = false

/**
 * ScaStatusRefreshOnReturn
 *
 * Mangopay appends `controlStatus` to the return URL of every SCA flow
 * (enrollment, consent, recipient registration). Its presence means the user
 * just came back from Mangopay, so our stored SCA status is about to change --
 * but the webhook that changes it is asynchronous and may land after this page
 * has already rendered.
 *
 * Reconciling explicitly here means the user never has to log out and back in
 * to see the result of the flow they just completed. Renders nothing.
 */
export function ScaStatusRefreshOnReturn() {
	const { controlStatus } = useHiddenGetParamsContext()
	const queryClient = useQueryClient()

	const { mutate: refreshScaStatus } = useScaStatusRefresh()

	useEffect(() => {
		if (!controlStatus || hasReconciledScaThisSession) return
		hasReconciledScaThisSession = true

		refreshScaStatus(undefined, {
			// Also on error: the webhook may have landed in the meantime, so a
			// refetch is still worth doing.
			onSettled: () => {
				void queryClient.invalidateQueries({
					queryKey: ["UserContext"],
				})
			},
		})
	}, [controlStatus, refreshScaStatus, queryClient])

	return null
}
